<template>
  <div class="notification-container fixed z-50">
    <TransitionGroup name="notification" tag="div">
      <div
        v-for="notification in notifications"
        :key="notification.id"
        :class="getNotificationClasses(notification.type)"
        class="notification-toast rounded-lg pointer-events-auto overflow-hidden"
      >
        <div class="notification-content">
          <div class="notification-inner">
            <div class="notification-icon-wrapper">
              <component :is="getIcon(notification.type)" :class="getIconClasses(notification.type)" class="notification-icon" />
            </div>
            <div class="notification-text">
              <p class="notification-title">
                {{ notification.title }}
              </p>
              <p class="notification-message">
                {{ notification.message }}
              </p>
            </div>
            <div class="notification-close-wrapper">
              <button
                @click="removeNotification(notification.id)"
                class="notification-close"
              >
                <span class="sr-only">Fechar</span>
                <XMarkIcon class="notification-close-icon" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </TransitionGroup>
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { useNotificationStore, type Notification } from '../stores/notification.ts'
import { CheckCircleIcon, ExclamationTriangleIcon, InformationCircleIcon, XCircleIcon, XMarkIcon } from '@heroicons/vue/24/outline'

const notificationStore = useNotificationStore()
const { notifications } = storeToRefs(notificationStore)
const { removeNotification } = notificationStore

const getNotificationClasses = (type: string) => {
  switch (type) {
    case 'success':
      return 'border-l-4 border-green-400'
    case 'error':
      return 'border-l-4 border-red-400'
    case 'warning':
      return 'border-l-4 border-yellow-400'
    case 'info':
      return 'border-l-4 border-blue-400'
    default:
      return 'border-l-4 border-gray-400'
  }
}

const getIcon = (type: string) => {
  switch (type) {
    case 'success':
      return CheckCircleIcon
    case 'error':
      return XCircleIcon
    case 'warning':
      return ExclamationTriangleIcon
    case 'info':
      return InformationCircleIcon
    default:
      return InformationCircleIcon
  }
}

const getIconClasses = (type: string) => {
  switch (type) {
    case 'success':
      return 'text-green-400'
    case 'error':
      return 'text-red-400'
    case 'warning':
      return 'text-yellow-400'
    case 'info':
      return 'text-blue-400'
    default:
      return 'text-gray-400'
  }
}
</script>

<style scoped>
/* Container - Mobile First */
.notification-container {
  /* Posicionar abaixo da topbar no mobile (60px header + safe-area + margem) */
  top: calc(60px + env(safe-area-inset-top, 0px) + 12px);
  left: 16px;
  right: 16px;
  width: calc(100% - 32px);
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* Toast base */
.notification-toast {
  background: var(--surface);
  border: 1px solid var(--border-color);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  width: 100%;
  max-width: 100%;
}

/* Content - Mobile First */
.notification-content {
  padding: 12px;
}

.notification-inner {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.notification-icon-wrapper {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  min-height: 20px;
}

.notification-icon {
  width: 20px;
  height: 20px;
}

.notification-text {
  flex: 1;
  min-width: 0;
  padding-top: 2px;
}

.notification-title {
  color: var(--bloop-text);
  font-size: 14px;
  font-weight: 600;
  line-height: 1.4;
  margin: 0;
}

.notification-message {
  color: var(--bloop-text-muted);
  font-size: 13px;
  line-height: 1.4;
  margin: 4px 0 0 0;
}

.notification-close-wrapper {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.notification-close {
  background: transparent;
  border: none;
  color: var(--bloop-text-muted);
  padding: 8px;
  min-width: 44px;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.notification-close:hover {
  color: var(--bloop-text);
  background: rgba(126, 211, 33, 0.1);
}

.notification-close:focus {
  outline: 2px solid var(--bloop-green);
  outline-offset: 2px;
}

.notification-close-icon {
  width: 18px;
  height: 18px;
}

/* Animações */
.notification-enter-active,
.notification-leave-active {
  transition: all 0.3s ease;
}

.notification-enter-from {
  opacity: 0;
  transform: translateY(-20px);
}

.notification-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}

/* Desktop (≥ 480px) */
@media (min-width: 480px) {
  .notification-container {
    top: 20px;
    right: 20px;
    left: auto;
    width: auto;
    max-width: 448px;
    min-width: 320px;
  }

  .notification-toast {
    max-width: 448px;
  }

  .notification-content {
    padding: 16px;
  }

  .notification-inner {
    gap: 12px;
  }

  .notification-icon {
    width: 20px;
    height: 20px;
  }

  .notification-title {
    font-size: 15px;
  }

  .notification-message {
    font-size: 14px;
    margin-top: 6px;
  }

  .notification-close {
    padding: 4px;
    min-width: auto;
    min-height: auto;
  }

  .notification-close-icon {
    width: 16px;
    height: 16px;
  }

  .notification-enter-from {
    transform: translateX(100%);
  }

  .notification-leave-to {
    transform: translateX(100%);
  }
}

/* Tablet e Desktop maior (≥ 768px) */
@media (min-width: 768px) {
  .notification-container {
    max-width: 480px;
  }

  .notification-toast {
    max-width: 480px;
  }
}
</style>
